import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Archive, Sparkle, DownloadSimple } from "@phosphor-icons/react";
import api, { apiErr } from "../api";

function RecapDetail({ recap, onBack }) {
  const [detail, setDetail] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [fmt, setFmt] = useState("landscape");
  const [imgUrl, setImgUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/my-seasons/${recap.id}`).then((r) => {
      setDetail(r.data);
      if (r.data.eligible_highlights?.length) setHighlight(r.data.eligible_highlights[0].key);
    }).catch((e) => toast.error(apiErr(e)));
  }, [recap.id]);

  const buildCard = useCallback(async () => {
    if (!highlight) return;
    setBusy(true);
    try {
      const { data } = await api.post("/my-seasons/card", { recap_id: recap.id, highlight_key: highlight, fmt });
      const res = await api.get(`/cards/img/${data.opaque_id}`, { responseType: "blob" });
      setImgUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
      toast.success("Season card ready");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }, [highlight, fmt, recap.id]);

  const download = () => {
    if (!imgUrl) return;
    const a = document.createElement("a");
    a.href = imgUrl; a.download = `season-card-${fmt}.png`; a.click();
  };

  if (!detail) return <div className="ff-panel rounded-md p-8 text-center text-ff-muted">Loading recap…</div>;

  return (
    <div className="space-y-5" data-testid="recap-detail">
      <button onClick={onBack} className="text-sm text-ff-brass hover:text-ff-gold">← All seasons</button>
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">{detail.season_name}</h1>
        <p className="mt-1 text-ff-gold">{detail.category} · Final #{detail.final_position} of {detail.eligible_population}</p>
      </div>

      <div className="ff-panel rounded-md p-5" data-testid="recap-narrative">
        {(detail.narrative || []).map((n, i) => <p key={i} className="mb-2 text-ff-parchment/90">{n}</p>)}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="recap-stats">
        {[
          ["Seasonal Score", detail.seasonal_score],
          ["Best Expedition", detail.best_expedition || "—"],
          ["Highest Mastery", detail.best_mastery_gain || "—"],
          ["Most-Used Crew", detail.most_used_crew || "—"],
          ["Best Skill Score", detail.best_skill_score],
          ["Daily Streak", detail.longest_daily_streak],
          ["Daily Completed", detail.daily_completed],
          ["Rivals Overtaken", detail.rivals_overtaken],
          ["Final Title", detail.final_title || "—"],
        ].map(([label, val]) => (
          <div key={label} className="ff-panel rounded-md p-3">
            <div className="text-xs uppercase tracking-widest text-ff-brass">{label}</div>
            <div className="mt-0.5 font-semibold text-ff-parchment">{String(val)}</div>
          </div>
        ))}
      </div>

      <div className="ff-panel rounded-md p-5" data-testid="season-card-builder">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold"><Sparkle size={20} weight="duotone" /> Build a shareable season card</h2>
        <p className="mt-1 text-sm text-ff-muted">Choose one verified highlight, then generate.</p>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="highlight-selector">
          {(detail.eligible_highlights || []).map((h) => (
            <button key={h.key} onClick={() => setHighlight(h.key)} data-testid={`highlight-${h.key}`}
              className={`rounded-full px-3 py-1.5 text-sm transition-colors ${highlight === h.key ? "bg-ff-gold text-ff-bg" : "border border-ff-brass/25 text-ff-muted hover:text-ff-parchment"}`}>
              {h.label}: {h.value}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {["landscape", "square", "story"].map((v) => (
            <button key={v} onClick={() => setFmt(v)} data-testid={`season-fmt-${v}`}
              className={`rounded-full px-3 py-1 text-xs transition-colors ${fmt === v ? "bg-ff-brass text-ff-bg" : "border border-ff-brass/25 text-ff-muted"}`}>{v}</button>
          ))}
        </div>
        <button onClick={buildCard} disabled={busy || !highlight} data-testid="season-card-generate"
          className="mt-4 rounded-sm bg-ff-gold px-4 py-2.5 font-semibold text-ff-bg hover:bg-ff-brass disabled:opacity-40">
          {busy ? "Rendering…" : "Generate Season Card"}
        </button>

        {imgUrl && (
          <div className="mt-4">
            <img src={imgUrl} alt="Season card" data-testid="season-card-preview" className="mx-auto max-h-[500px] w-auto max-w-full rounded-sm" />
            <button onClick={download} data-testid="season-card-download" className="mt-3 flex items-center gap-1.5 rounded-sm border border-ff-brass/40 px-4 py-2 font-semibold text-ff-parchment hover:border-ff-gold hover:text-ff-gold"><DownloadSimple size={18} /> Download PNG</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MySeasons() {
  const [recaps, setRecaps] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/my-seasons").then((r) => setRecaps(r.data.recaps)).catch(() => {}); }, []);

  if (selected) return <RecapDetail recap={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="space-y-5" data-testid="my-seasons-page">
      <div className="flex items-center gap-2">
        <Archive size={26} weight="duotone" className="text-ff-gold" />
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">My Season History</h1>
      </div>
      {recaps.length === 0 && <p className="text-ff-muted" data-testid="my-seasons-empty">Your first season recap will appear here once the season is finalized.</p>}
      {recaps.map((r) => (
        <button key={r.id} onClick={() => setSelected(r)} data-testid={`recap-${r.id}`}
          className="ff-panel block w-full rounded-md p-5 text-left transition-colors hover:border-ff-gold">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-ff-parchment">{r.season_name}</h2>
            <span className="text-ff-gold">#{r.final_position}</span>
          </div>
          <p className="mt-1 text-sm text-ff-muted">{r.category}{r.final_title ? ` · ${r.final_title}` : ""}</p>
        </button>
      ))}
    </div>
  );
}
